/**
 * Marques du planning multi-marques (docs/ux-v2/SPEC.md § 5.2, PL5, PL6, PL11).
 *
 * Source : `mondayMindbody.uxV2.brands` (injecté en PHP : réglages de chaque marque, surcharges du BO). Aucune
 * valeur propre à une marque ici : à défaut de donnée, nom tiré des studios du site Mindbody, couleur encre, et pas
 * de lien vers le site de la marque (clic désactivé, SPEC § 5.2 point 2).
 *
 * Couleurs : le liseré et la pastille prennent la couleur de la marque telle quelle ; tout texte à la couleur d'une
 * marque (nom du cours, étiquette de marque) est assombri juste assez pour 4,5:1 sur le blanc de la carte et sur le
 * fond vert d'une carte « Réservé » (SPEC PL5, § 2.4 point 7).
 */
import { AA, ensureContrast, normalizeHex } from '../color';

/** Marque telle qu'injectée par le serveur (forme SPEC § 5.2, champs tous facultatifs côté client). */
export interface RawBrand {
  siteId?: number | string;
  key?: string;
  name?: string;
  color?: string;
  textColor?: string;
  url?: string;
  /** Accroche de la marque (réglage « Accroche » du BO) : encart « groupe » du planning. */
  tagline?: string;
  isCurrent?: boolean;
}

export interface PlanningBrand {
  siteId: string;
  name: string;
  /** Liseré, pastille de légende et d'étiquette. */
  color: string;
  /** Texte à la couleur de la marque, AA sur blanc et sur le fond « Réservé » (calculé ici). */
  textColor: string;
  /** Page de réservation de la marque, où s'ouvrent ses cours (nouvel onglet) ; null = pas de lien connu. */
  url: string | null;
  /** Accroche de la marque, '' sans réglage (encart « groupe » du planning, Filters.tsx). */
  tagline: string;
  isCurrent: boolean;
}

export interface BrandPalette {
  /** Couleur du site courant (jeton --mmv2-brand). */
  currentColor: string;
  /** Couleur neutre des marques sans couleur connue (encre). */
  ink: string;
  /** Fonds sur lesquels le texte de marque doit rester lisible. */
  backgrounds: string[];
}

/** Couleur de texte AA sur tous les fonds donnés (assombrie pas à pas si besoin). */
export function readableBrandText(color: string, backgrounds: string[]): string {
  return backgrounds.reduce((text, background) => ensureContrast(text, background, AA), color);
}

/** URL http(s) ou chemin absolu exploitable ; null sinon. */
export function sanitizeUrl(url: string | undefined | null): string | null {
  if (typeof url !== 'string') return null;
  const value = url.trim();
  if (value === '') return null;
  if (/^https?:\/\//i.test(value) || value.startsWith('/')) return value;
  return null;
}

/** Premier mot d'un nom de studio Mindbody (« RIISE Boetie » → « RIISE », « Punch - Louvre » → « Punch »). */
function firstWord(name: string): string {
  return (name.trim().split(/[\s-]+/)[0] ?? '').trim();
}

/** Nom de repli d'une marque : premier mot le plus fréquent des noms de ses studios. */
export function brandNameFromLocations(locationNames: string[]): string {
  const counts = new Map<string, number>();
  locationNames.forEach((name) => {
    const word = firstWord(name);
    if (word) counts.set(word, (counts.get(word) ?? 0) + 1);
  });
  let best = '';
  let bestCount = 0;
  counts.forEach((count, word) => {
    if (count > bestCount) {
      best = word;
      bestCount = count;
    }
  });
  return best;
}

export interface ResolveBrandsInput {
  /** Sites affichés, dans l'ordre de la réponse du serveur. */
  siteIds: Array<string | number>;
  raw: RawBrand[] | undefined | null;
  currentSiteId: string;
  /** Réglage « Nom de la marque » du site courant (customization.brandName). */
  currentName: string;
  /** Noms des studios de chaque site, pour le nom de repli. */
  locationNamesBySite: Record<string, string[]>;
  palette: BrandPalette;
}

/** Une entrée par site affiché, dans l'ordre : site courant d'abord, puis l'ordre du serveur. */
export function resolveBrands({
  siteIds,
  raw,
  currentSiteId,
  currentName,
  locationNamesBySite,
  palette,
}: ResolveBrandsInput): PlanningBrand[] {
  const rawBySite = new Map<string, RawBrand>();
  (Array.isArray(raw) ? raw : []).forEach((brand) => {
    if (brand && brand.siteId !== undefined && brand.siteId !== null) rawBySite.set(String(brand.siteId), brand);
  });

  const ordered = [...new Set(siteIds.map(String))].sort((a, b) =>
    a === currentSiteId ? -1 : b === currentSiteId ? 1 : 0,
  );

  return ordered.map((siteId) => {
    const source = rawBySite.get(siteId) ?? {};
    const isCurrent = siteId === currentSiteId;
    const name =
      (source.name ?? '').trim() ||
      (isCurrent ? currentName.trim() : '') ||
      brandNameFromLocations(locationNamesBySite[siteId] ?? []) ||
      `Site ${siteId}`;
    // Site courant : la couleur de marque de la page (jeton du thème, sinon réglage), pour rester raccord avec
    // l'en-tête du site ; autres marques : leur réglage.
    const color = isCurrent ? palette.currentColor : (normalizeHex(source.color) ?? palette.ink);
    // `textColor` du serveur = texte posé SUR la couleur de marque (readableOn) : il ne sert pas ici, où le
    // texte est à la couleur de la marque sur fond clair.
    return {
      siteId,
      name,
      color,
      textColor: readableBrandText(color, palette.backgrounds),
      url: isCurrent ? null : sanitizeUrl(source.url),
      tagline: typeof source.tagline === 'string' ? source.tagline.replace(/\s+/g, ' ').trim() : '',
      isCurrent,
    };
  });
}

/**
 * Site d'une marque (encart « groupe » du planning) : le dossier parent de sa page de réservation, qui est
 * {accueil}/reservation/ (BrandService::defaultReservationUrl) : « https://riise-studios.com/reservation/ » →
 * « https://riise-studios.com/ », staging en sous-dossier compris (« https://aptekaa.dev/monday/reservation/ » →
 * « https://aptekaa.dev/monday/ »). null sans adresse exploitable ; un chemin relatif (même site) n'a pas de sens pour
 * une autre marque.
 */
export function brandSiteUrl(reservationUrl: string | null): string | null {
  if (!reservationUrl || !/^https?:\/\//i.test(reservationUrl)) return null;
  try {
    const url = new URL(reservationUrl);
    url.hash = '';
    url.search = '';
    return new URL(url.pathname.endsWith('/') ? '../' : './', url).href;
  } catch {
    return null;
  }
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Nom court d'un studio : sans le nom de marque en tête (« dynamo Boétie » → « Boétie », « Punch - Louvre » →
 * « Louvre »), première lettre en capitale (« Punch boxing studio Marseille » → « Boxing studio Marseille »). Les
 * préfixes viennent des noms de marque connus (réglages), jamais d'une liste codée.
 */
export function shortStudioName(locationName: string, brandNames: string[]): string {
  const name = (locationName ?? '').trim();
  const prefixes = [...new Set(brandNames.flatMap((brand) => [brand.trim(), firstWord(brand)]).filter(Boolean))].sort(
    (a, b) => b.length - a.length,
  );
  for (const prefix of prefixes) {
    const match = new RegExp(`^${escapeRegExp(prefix)}\\s*[-–—]?\\s+`, 'i').exec(name);
    if (match && name.length > match[0].length) {
      const rest = name.slice(match[0].length).trim();
      return rest.charAt(0).toLocaleUpperCase('fr') + rest.slice(1);
    }
  }
  return name;
}
